import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Eye, Edit, Trash2, Lock } from 'lucide-react'
import { format } from 'date-fns'
import type { EmailTemplate } from '../types'

interface TemplateTableProps {
  templates: EmailTemplate[]
  onView: (template: EmailTemplate) => void
  onEdit: (template: EmailTemplate) => void
  onDelete: (template: EmailTemplate) => void
}

const categoryLabels: Record<string, { label: string; className: string }> = {
  transactional: { label: 'Transactional', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  notification: { label: 'Notification', className: 'bg-purple-50 text-purple-700 border-purple-200' },
  engagement: { label: 'Engagement', className: 'bg-green-50 text-green-700 border-green-200' },
  custom: { label: 'Custom', className: 'bg-gray-50 text-gray-700 border-gray-200' },
}

export function TemplateTable({
  templates,
  onView,
  onEdit,
  onDelete,
}: TemplateTableProps) {
  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Template</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Variables</TableHead>
            <TableHead>Updated</TableHead>
            <TableHead className="w-32">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {templates.map((template) => {
            const category = categoryLabels[template.category] || categoryLabels.custom
            const variables = typeof template.variables === 'string'
              ? JSON.parse(template.variables)
              : template.variables

            return (
              <TableRow key={template.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {template.isSystem && (
                      <span title="System template">
                        <Lock className="h-4 w-4 text-muted-foreground" />
                      </span>
                    )}
                    <div>
                      <p className="font-medium">{template.name}</p>
                      <p className="text-sm text-muted-foreground">{template.slug}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={category.className}>
                    {category.label}
                  </Badge>
                </TableCell>
                <TableCell>
                  {template.isActive ? (
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      Active
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">
                      Inactive
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {Array.isArray(variables) && variables.slice(0, 3).map((v: string) => (
                      <code key={v} className="text-xs bg-muted px-1 py-0.5 rounded">
                        {`{{${v}}}`}
                      </code>
                    ))}
                    {Array.isArray(variables) && variables.length > 3 && (
                      <span className="text-xs text-muted-foreground">
                        +{variables.length - 3} more
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {format(new Date(template.updatedAt), 'MMM d, yyyy')}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onView(template)}
                      title="View template"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit(template)}
                      title="Edit template"
                      disabled={template.isSystem}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    {!template.isSystem && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onDelete(template)}
                        title="Delete template"
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            )
          })}
          {templates.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                No templates found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}
